import { scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import type { AtlasRow } from '../types';

interface AtlasMapProps {
  rows: AtlasRow[];
  totalSignals: number;
  minN: number;
}

export function AtlasMap({ rows, totalSignals, minN }: AtlasMapProps) {
  const width = 760;
  const height = 390;
  const maxN = max(rows, (d) => d.n) || 1;
  const x = scaleLinear().domain([0, Math.max(1, rows.length - 1)]).range([80, width - 80]);
  const y = scaleLinear().domain([0, 1]).range([height - 70, 52]);
  const r = scaleLinear().domain([1, maxN]).range([18, 52]);

  if (!rows.length) {
    return (
      <div className="emptyAtlas">
        <div className="emptyOrb" />
        <h3>No public constellation yet</h3>
        <p>Aggregates appear once at least {minN} human signals exist for a value-conflict pair. Total raw signals: {totalSignals}.</p>
      </div>
    );
  }

  return (
    <svg className="atlasSvg" viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby="atlas-title atlas-desc">
      <title id="atlas-title">Doubt Atlas commons map</title>
      <desc id="atlas-desc">Bubbles show value conflicts. Higher bubbles are more torn, larger bubbles have more signals.</desc>
      <defs>
        <linearGradient id="atlasGlow" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#67e8f9" />
          <stop offset="1" stopColor="#f0abfc" />
        </linearGradient>
        <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <line x1="58" x2={width - 52} y1={y(0.5)} y2={y(0.5)} stroke="rgba(255,255,255,.16)" strokeDasharray="8 8" />
      <text x="28" y={y(0.93)} className="axisText">torn</text>
      <text x="28" y={y(0.1)} className="axisText">settled</text>
      {rows.map((row, index) => {
        const cx = x(index);
        const cy = y(row.mean_tornness);
        const radius = r(row.n);
        const certaintyOffset = (row.mean_certainty - 0.5) * 34;
        return (
          <g key={row.value_pair} tabIndex={0} className="atlasNode" aria-label={`${row.value_pair}, ${row.n} signals`}>
            <circle cx={cx} cy={cy} r={radius + 12} fill="url(#atlasGlow)" opacity={0.08 + row.rock_ratio * 0.16} filter="url(#softGlow)" />
            <circle cx={cx} cy={cy} r={radius} fill="rgba(255,255,255,.07)" stroke="url(#atlasGlow)" strokeWidth="2" />
            <circle cx={cx + certaintyOffset} cy={cy - certaintyOffset / 4} r="5" fill="#fff" opacity=".85" />
            <text x={cx} y={cy + radius + 24} textAnchor="middle" className="nodeText">
              {row.foundation_a.split('/')[0]} × {row.foundation_b.split('/')[0]}
            </text>
            <title>{`${row.value_pair}\n${row.n} signals\nTornness ${(row.mean_tornness * 100).toFixed(0)}%\nCertainty ${(row.mean_certainty * 100).toFixed(0)}%\nRock ratio ${(row.rock_ratio * 100).toFixed(0)}%`}</title>
          </g>
        );
      })}
    </svg>
  );
}
